/**
 * Canvas de assinatura à mão (dedo, caneta ou mouse) para a retirada na
 * expedição. Pointer Events + `touch-action: none` para o dedo não rolar a
 * página enquanto assina; escala pelo devicePixelRatio para o traço não
 * sair serrilhado no celular. Expõe por ref: `limpar()`, `toDataURL()` e
 * `estaVazia()`; avisa `onChange(vazia)` a cada traço.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import styled from "styled-components";

const Moldura = styled.div`
  position: relative;
  border: 1px dashed var(--border);
  border-radius: 10px;
  /* Sempre papel branco com tinta escura, inclusive no tema escuro: a
     assinatura vai para o comprovante impresso e precisa ser legível. */
  background: #fff;
  overflow: hidden;
`;

const Tela = styled.canvas`
  display: block;
  width: 100%;
  touch-action: none;
  cursor: crosshair;
`;

const Dica = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
  color: #8a8a8a;
  font-size: 13px;
`;

const Linha = styled.div`
  position: absolute;
  left: 8%;
  right: 8%;
  bottom: 22%;
  border-bottom: 1px solid #c9c9c9;
  pointer-events: none;
`;

const AssinaturaCanvas = forwardRef(function AssinaturaCanvas({ altura = 180, onChange }, ref) {
  const canvasRef = useRef(null);
  const desenhando = useRef(false);
  const tracos = useRef(0);
  const [vazia, setVazia] = useState(true);

  // Ajusta a resolução interna ao tamanho visível × DPR (uma vez por
  // largura); redimensionar apaga o desenho, então só roda no mount/resize.
  const preparar = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const largura = c.clientWidth || 300;
    c.width = Math.round(largura * dpr);
    c.height = Math.round(altura * dpr);
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111";
  }, [altura]);

  useEffect(() => {
    preparar();
    const aoRedimensionar = () => {
      // Só reprepara se estiver vazio: redimensionar apaga o traço.
      if (tracos.current === 0) preparar();
    };
    window.addEventListener("resize", aoRedimensionar);
    return () => window.removeEventListener("resize", aoRedimensionar);
  }, [preparar]);

  const marcar = (nova) => {
    if (nova !== vazia) {
      setVazia(nova);
      onChange?.(nova);
    }
  };

  const ponto = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const iniciar = (e) => {
    e.preventDefault();
    desenhando.current = true;
    canvasRef.current.setPointerCapture?.(e.pointerId);
    const ctx = canvasRef.current.getContext("2d");
    const p = ponto(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    // Um toque sem arrastar ainda vale um ponto.
    ctx.lineTo(p.x + 0.1, p.y + 0.1);
    ctx.stroke();
    tracos.current += 1;
    marcar(false);
  };

  const mover = (e) => {
    if (!desenhando.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = ponto(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const terminar = (e) => {
    if (!desenhando.current) return;
    desenhando.current = false;
    canvasRef.current.releasePointerCapture?.(e.pointerId);
  };

  useImperativeHandle(ref, () => ({
    limpar() {
      tracos.current = 0;
      preparar();
      marcar(true);
    },
    toDataURL() {
      return tracos.current === 0 ? "" : canvasRef.current.toDataURL("image/png");
    },
    estaVazia() {
      return tracos.current === 0;
    },
  }));

  return (
    <Moldura>
      <Tela
        ref={canvasRef}
        style={{ height: altura }}
        onPointerDown={iniciar}
        onPointerMove={mover}
        onPointerUp={terminar}
        onPointerCancel={terminar}
        onPointerLeave={terminar}
        aria-label="Área de assinatura"
        role="img"
      />
      <Linha />
      {vazia && <Dica>Assine aqui</Dica>}
    </Moldura>
  );
});

export default AssinaturaCanvas;
